import React from 'react'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'

export interface MiniInsightsCardProps {
  beat: { [key: string]: any }
  DismissedBeat?: React.ReactNode
  headerTextOverride?: React.ReactElement | string
  height?: number | string
  setRefs?: (...args: any[]) => void
  subHeader?: React.ReactElement | string
}

interface MiniInsightsProps extends MiniInsightsCardProps {
  children: React.ReactNode
}

export const MiniInsightCard: React.FC<MiniInsightsProps> = ({
  beat,
  children,
  DismissedBeat,
  headerTextOverride,
  setRefs,
  subHeader,
  height,
}) => {
  const [maxLines, setMaxLines] = React.useState<number | null>(null)

  // This Function is needed to determine the number of lines that can be visible so that the line clamp can be dynamic
  const calculateNumberOfLinesBeforeElepsisIsNeeded = () => {
    const cardHeight = document.getElementsByClassName('mx-mini-insight-card')[0].clientHeight
    const combinedHeight =
      document.getElementsByClassName('mx-mini-insight-card-header')[0].clientHeight +
      document.getElementsByClassName('mx-mini-insight-card-content-body')[0].clientHeight

    // Subtract the total height from the combined height of the header and body components including bottom padding to determine the left over space for the description component
    const totalHeight = cardHeight - combinedHeight - 24

    // divide total height from the line height. It's floored to find the actual full text lines that can be visible in the description component.
    setMaxLines(Math.floor(totalHeight / 20))
  }

  React.useEffect(() => {
    calculateNumberOfLinesBeforeElepsisIsNeeded()

    //re-run on window change
    window.addEventListener('resize', calculateNumberOfLinesBeforeElepsisIsNeeded)

    return () => window.removeEventListener('resize', calculateNumberOfLinesBeforeElepsisIsNeeded)
  }, [])

  return (
    <Card
      aria-labelledby={beat.guid}
      className="mx-mini-insight-card"
      ref={setRefs}
      sx={{ height: height, pb: 24, pt: 0, px: 0 }}
    >
      {beat.dismissed_at ? (
        DismissedBeat
      ) : (
        <React.Fragment>
          <CardHeader
            className="mx-mini-insight-card-header"
            subheader={subHeader}
            sx={{ gap: 8 }}
            title={headerTextOverride ? headerTextOverride : beat?.title}
            titleTypographyProps={{ fontSize: 18, fontWeight: 600, variant: 'h3' }}
          />

          <CardContent
            className="mx-mini-insight-card-content"
            sx={{
              overflow: 'hidden',
              p: 0,
              pb: 24,
              width: '100%',
            }}
          >
            <Box className="mx-mini-insight-card-content-body">{children}</Box>

            {/* Wrapper is necessary to make sure the inner boxes dangerouslySetInnerHtml's children obey the overflow hidden */}
            <Box sx={{ p: 24 }}>
              <Box
                className="test-class"
                dangerouslySetInnerHTML={{ __html: beat.html_description }}
                data-ui-test="beat-description"
                sx={{
                  display: '-webkit-box',
                  fontSize: 13,
                  lineHeight: '20px',
                  mx: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  WebkitLineClamp: maxLines,
                  WebkitBoxOrient: 'vertical',
                  '& > p': {
                    m: 0,
                  },
                }}
              />
            </Box>
          </CardContent>
        </React.Fragment>
      )}
    </Card>
  )
}
